import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitFeedback } from "@/api";

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z"
    />
  </svg>
);

export const PositiveOutcome = ({ slug, rating, googleReviewUrl, onContinue }) => {
  const [sending, setSending] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await submitFeedback(slug, { rating });
      window.open(googleReviewUrl, "_blank", "noopener,noreferrer");
      onContinue();
    } catch {
      toast.error("No se ha podido enviar. Inténtalo de nuevo.");
      setSending(false);
    }
  };

  return (
    <motion.div
      key="positive"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="w-full flex flex-col items-center text-center gap-8"
      data-testid="positive-outcome"
    >
      <div className="space-y-4">
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 14, delay: 0.1 }}
          className="mx-auto h-16 w-16 rounded-full bg-[#FDF3E3] border border-[#F0E3C8] flex items-center justify-center"
        >
          <span className="text-3xl" role="img" aria-label="corazón">
            ❤️
          </span>
        </motion.div>
        <h2
          data-testid="positive-heading"
          className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1C1917] leading-snug"
        >
          ¡Nos alegra mucho!
        </h2>
        <p className="text-base text-[#57534E] leading-relaxed max-w-xs mx-auto">
          Si has disfrutado de tu experiencia, nos ayudaría mucho que
          compartieras tu opinión en Google.
        </p>
      </div>

      <motion.a
        href={googleReviewUrl}
        onClick={handleClick}
        data-testid="google-review-button"
        whileHover={sending ? undefined : { scale: 1.02 }}
        whileTap={sending ? undefined : { scale: 0.97 }}
        transition={{ duration: 0.2 }}
        aria-disabled={sending}
        className={`w-full flex items-center justify-center gap-3 bg-[#1A73E8] text-white font-medium text-base rounded-xl px-6 py-4 shadow-[0_10px_30px_-10px_rgba(26,115,232,0.45)] transition-colors duration-200 ${
          sending ? "opacity-80 cursor-wait" : "hover:bg-[#1557B0] cursor-pointer"
        }`}
      >
        <span className="bg-white rounded-full p-1">
          <GoogleMark />
        </span>
        Dejar reseña en Google
        {sending ? (
          <Loader2 className="h-4 w-4 animate-spin" data-testid="google-review-loading" />
        ) : (
          <ArrowUpRight className="h-4 w-4 opacity-80" />
        )}
      </motion.a>

      <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E]">
        Solo te llevará un minuto
      </p>
    </motion.div>
  );
};
